import { ConvergenceMatrix, MatrixCell } from "../../api";

interface Props {
  matrix: ConvergenceMatrix | null;
}

/** Tone for one cell in the heatmap. Color follows the action:
 *  buy = success (green), sell = danger (red), close = primary (blue),
 *  veto = warn (amber). Empty (no recent signal) = muted neutral. */
function cellTone(c: MatrixCell): {
  bg: string;
  text: string;
  label: string;
} {
  if (c.action == null) {
    return { bg: "bg-bg-hover/40", text: "text-fg-muted/60", label: "·" };
  }
  switch (c.action) {
    case "buy":
      return { bg: "bg-success/25", text: "text-success", label: "B" };
    case "sell":
      return { bg: "bg-danger/25", text: "text-danger", label: "S" };
    case "close":
      return { bg: "bg-primary/25", text: "text-primary", label: "C" };
    case "veto":
      return { bg: "bg-warn/25", text: "text-warn", label: "V" };
  }
}

export function ConvergenceHeatmap({ matrix }: Props) {
  return (
    <section className="flex h-full flex-col rounded-xl border border-border bg-surface">
      <header className="flex items-baseline justify-between border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.15em] text-fg-muted">
          Convergence matrix
        </h3>
        <span className="font-mono text-xs text-fg-muted">
          {matrix == null
            ? "—"
            : `${matrix.mode} · ${matrix.window_seconds}s window`}
        </span>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {matrix?.mode === "strict" &&
          matrix.warnings?.map((w) => (
            <p
              key={w}
              role="alert"
              className="mb-2 rounded border border-warn/50 bg-warn/10 px-3 py-1.5 text-xs text-warn"
            >
              {w}
            </p>
          ))}
        {matrix == null ? (
          <p className="text-sm text-fg-muted">no matrix data</p>
        ) : matrix.symbols.length === 0 ? (
          // An empty matrix is the normal idle state - votes only sit here
          // between a strategy signalling and the rest agreeing (or the
          // window expiring). It used to say "no symbols configured - set
          // ENABLED_STRATEGIES in env", which read as a fault.
          <p className="text-sm text-fg-muted">
            {matrix.mode === "single" ? (
              "single-fire mode — every signal executes on its own"
            ) : (
              <>
                no votes in flight · waiting on{" "}
                <span className="font-mono">{matrix.enabled_strategies.join(", ")}</span>
              </>
            )}
          </p>
        ) : (
          <table className="w-full border-separate" style={{ borderSpacing: "4px" }}>
            <thead>
              <tr>
                <th className="text-left text-[10px] font-medium uppercase tracking-wider text-fg-muted">
                  symbol
                </th>
                {matrix.symbols[0]?.strategies.map((s) => (
                  <th
                    key={s.strategy_id}
                    className="px-1 text-center text-[10px] font-medium uppercase tracking-wider text-fg-muted"
                    title={s.strategy_id}
                  >
                    <span className="block max-w-[5rem] truncate">
                      {s.strategy_id}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.symbols.map((row) => (
                <tr key={row.symbol}>
                  <td className="font-mono text-sm font-semibold">
                    {row.symbol}
                  </td>
                  {row.strategies.map((cell, i) => {
                    const tone = cellTone(cell);
                    // Validated on another symbol: shown, but does not count (F61).
                    const na = cell.applicable === false;
                    return (
                      <td key={i} className={na ? "text-center opacity-40" : "text-center"}>
                        <span
                          className={`flex h-9 w-full items-center justify-center rounded font-mono text-sm font-bold ${tone.bg} ${tone.text}`}
                          title={
                            (na ? "does not apply to this symbol · " : "") +
                            (cell.action == null
                              ? "no recent signal"
                              : `${cell.action}${cell.price != null ? ` @ ${cell.price}` : ""}${cell.age_seconds != null ? ` (${cell.age_seconds}s ago)` : ""}`)
                          }
                        >
                          {na && cell.action == null ? "–" : tone.label}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <footer className="border-t border-border px-5 py-2 text-[10px] uppercase tracking-wider text-fg-muted">
        <span className="mr-3"><span className="text-success">B</span> buy</span>
        <span className="mr-3"><span className="text-danger">S</span> sell</span>
        <span className="mr-3"><span className="text-primary">C</span> close</span>
        <span><span className="text-warn">V</span> veto</span>
      </footer>
    </section>
  );
}
